import { useState } from "react"
import { faCalendar, faClose, faFilter } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface ReservationDateFiltersPopupProps {
  initialFilters: {
    startDate: string
    endDate: string
  }
  onApplyFilters: (filters: { startDate: string; endDate: string }) => void
  onClose: () => void
}

export const ReservationDateFiltersPopup: React.FC<ReservationDateFiltersPopupProps> = ({
  initialFilters,
  onApplyFilters,
  onClose,
}) => {
  const { t } = useTranslation("restaurantReservations")

  const [startDate, setStartDate] = useState(initialFilters.startDate)

  const [endDate, setEndDate] = useState(initialFilters.endDate)

  const isValidPeriod = Boolean(startDate && endDate && startDate <= endDate)

  const handleStartDateChange = (value: string) => {
    if (!value) return
    setStartDate(value)
    if (!endDate || endDate < value) setEndDate(value)
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("filters.title")}
              icon={faFilter}
              boxShadow="none"
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    icon={faClose}
                    typeButton="button"
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:restaurantReservations.dateFiltersPopup.close"
                  />
                </div>
              }
            />

            <GetSpace top={5} />

            <div className="grid grid-cols-1 gap-3 p-2 md:grid-cols-2">
              <InputField
                icon={faCalendar}
                value={startDate}
                type="date"
                required
                placeholder={t("fields.start_date")}
                onChange={(event) => handleStartDateChange(event.target.value)}
                descriptionKey="interactionDescriptions:restaurantReservations.dateFiltersPopup.startDate"
              />

              <InputField
                icon={faCalendar}
                value={endDate}
                type="date"
                min={startDate}
                required
                placeholder={t("fields.end_date")}
                onChange={(event) => {
                  const value = event.target.value
                  if (value && value >= startDate) setEndDate(value)
                }}
                descriptionKey="interactionDescriptions:restaurantReservations.dateFiltersPopup.endDate"
              />
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                label={t("actions.apply_filters")}
                disabled={!isValidPeriod}
                onClick={() => {
                  if (isValidPeriod) onApplyFilters({ startDate, endDate })
                }}
                descriptionKey="interactionDescriptions:restaurantReservations.dateFiltersPopup.apply"
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
